import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";

export interface DispatchEvent {
  id: string;
  dispatch_id: string;
  actor_id: string | null;
  event_type: string;
  payload: any;
  created_at: string;
  actor_name?: string;
}

export function useDispatchEvents(dispatchId: string | undefined) {
  const [events, setEvents] = useState<DispatchEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!dispatchId) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("direct_dispatch_events" as any)
      .select("id, dispatch_id, actor_id, event_type, payload, created_at")
      .eq("dispatch_id", dispatchId)
      .order("created_at", { ascending: false })
      .limit(200);
    const list = (data as any[] | null) ?? [];
    const actorIds = Array.from(new Set(list.map((e) => e.actor_id).filter(Boolean)));
    let nameMap = new Map<string, string>();
    if (actorIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", actorIds);
      (profs ?? []).forEach((p: any) => {
        nameMap.set(p.id, [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "User");
      });
    }
    setEvents(list.map((e) => ({ ...e, actor_name: e.actor_id ? nameMap.get(e.actor_id) ?? "User" : "System" })));
    setLoading(false);
  }, [dispatchId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!dispatchId) return;
    const channel = supabase
      .channel(`dispatch-events-${dispatchId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "direct_dispatch_events", filter: `dispatch_id=eq.${dispatchId}` },
        () => refresh()
      )
      .subscribe((status) => { if (status === "SUBSCRIBED") refresh(); });
    return () => { supabase.removeChannel(channel); };
  }, [dispatchId, refresh]);
  useRealtimeSelfHeal(refresh, !!dispatchId);


  return { events, loading, refresh };
}
